import WowIcon, { TWowIconSize } from './WowIcon';
import cx from 'classnames';
import { t } from 'i18next';
import { CSSProperties, FC } from 'react';
import { isActiveTalentNode, isChoiceNode, isTieredApexTalent } from '../../../../common/talentHelpers';
import type { IBaseTraitNodeEntry, IOptionalChosenTraitNode } from '../../../../types/talentNodeTypes';
import { WowheadLink } from './WowheadLink';

type TTalentWithViewIndex = IOptionalChosenTraitNode & { viewIndex?: number };
export type TCompareBadgeTone = 'added' | 'removed' | 'modified';
export type TCompareBadge = {
  label: string;
  tone: TCompareBadgeTone;
};

export type TApexSatelliteTalentEntry = {
  entry: IBaseTraitNodeEntry;
  entryRank: number;
  cumulativeRank: number;
  linkRank?: number;
  displayIcon: string;
  active: boolean;
};

const APEX_SATELLITE_DISPLAY_ICON_OVERRIDES: Record<number, string> = {
  // Dance of Midnight's supporting ranks currently point at a Demon Hunter placeholder icon in Blizzard/Wowhead data.
  1264351: 'spell_deathknight_runetap',
  1264405: 'inv_sword_07'
};

type TTalentIconProps = {
  talent: TTalentWithViewIndex;
  size: TWowIconSize;
  className?: string;
  iconOnly?: boolean;
  /**
   * If true, tiered apex talents render their primary entry with adjacent
   * tier icons. This is intended for full talent-tree views where the extra
   * spatial context is useful.
   */
  showApexTierIcons?: boolean;
  /**
   * If true, renders the icon as dimmed/unselected (as if the talent is not selected).
   */
  dimmed?: boolean;

  /**
   * If true, renders the icon as selected even when the talent has no entryIndex.
   * Used to highlight prerequisite nodes whose downstream child is selected,
   * or to show auto-granted nodes as active.
   */
  forceActive?: boolean;

  badge?: string;
  compareBadge?: TCompareBadge | null;

  showArrows?: boolean;

  showRemovedIcon?: boolean;
  showAddedIcon?: boolean;

  border?: 'removed' | 'added';

  hideRanks?: boolean;

  rankDiff?: number;

  addedRankDiffVariant?: 'highlight' | 'increased';
};

type TDisplayedTalentEntryOptions = {
  useApexPrimaryEntry?: boolean;
};

export function resolveDisplayedTalentEntryIndex(
  talent: TTalentWithViewIndex,
  options: TDisplayedTalentEntryOptions = {}
): number {
  if (options.useApexPrimaryEntry && isTieredApexTalent(talent) && typeof talent.viewIndex !== 'number') {
    return 0;
  }

  if (typeof talent.viewIndex === 'number') {
    return talent.viewIndex;
  }

  if (typeof talent.entryIndex === 'number') {
    return talent.entryIndex;
  }

  return 0;
}

export function getDisplayedTalentEntry(talent: TTalentWithViewIndex, options: TDisplayedTalentEntryOptions = {}) {
  return talent.node.entries[resolveDisplayedTalentEntryIndex(talent, options)] ?? talent.node.entries[0];
}

export function isTieredTalentNode(talent: TTalentWithViewIndex): boolean {
  return talent.node.type === ETraitNodeType.Tiered;
}

export function getTalentRankLabel(talent: TTalentWithViewIndex): string | null {
  if (typeof talent.entryIndex !== 'number') {
    return null;
  }

  if (isTieredTalentNode(talent)) {
    const totalEntries = talent.node.entries.length;

    const selected = talent.rank;

    if (!selected) {
      return '';
    }

    return `${selected}/${totalEntries + 1}`;
  }

  const displayedEntry = getDisplayedTalentEntry(talent);

  if (!displayedEntry || talent.rank == null) {
    return null;
  }

  return `${talent.rank}/${displayedEntry.maxRanks}`;
}

export function getApexSatelliteTalentEntries(talent: IOptionalChosenTraitNode): TApexSatelliteTalentEntry[] {
  if (!isTieredApexTalent(talent)) {
    return [];
  }

  const activeRank = talent.rank ?? 0;
  let cumulativeRank = 0;
  const satelliteEntries: TApexSatelliteTalentEntry[] = [];

  for (const entry of talent.node.entries) {
    const previousCumulativeRank = cumulativeRank;
    cumulativeRank += entry.maxRanks;

    if (cumulativeRank <= 1 || !entry.spell) {
      continue;
    }

    const entryRank = Math.min(Math.max(activeRank - previousCumulativeRank, 1), entry.maxRanks);

    satelliteEntries.push({
      entry,
      entryRank,
      cumulativeRank,
      linkRank: entry.maxRanks > 1 ? entryRank : undefined,
      displayIcon: getApexSatelliteDisplayIcon(entry),
      active: activeRank > previousCumulativeRank
    });
  }

  return satelliteEntries;
}

function getApexSatelliteDisplayIcon(entry: IBaseTraitNodeEntry): string {
  const spell = entry.spell;

  if (!spell) {
    return 'inv_misc_questionmark';
  }

  return APEX_SATELLITE_DISPLAY_ICON_OVERRIDES[spell.id] ?? spell.icon;
}

export const TalentIcon: FC<TTalentIconProps> = (props: TTalentIconProps) => {
  const {
    talent,
    size,
    className,
    iconOnly,
    dimmed = true,
    badge,
    compareBadge,
    showArrows = false,
    showAddedIcon,
    showRemovedIcon,
    border,
    hideRanks = false,
    rankDiff,
    addedRankDiffVariant,
    forceActive = false,
    showApexTierIcons = false
  } = props;
  if (!talent.node.entries.length) {
    throw new Error('Talent has no entries');
  }

  const displayedEntryOptions = { useApexPrimaryEntry: showApexTierIcons };
  const displayedEntry = getDisplayedTalentEntry(talent, displayedEntryOptions);
  const { spell } = displayedEntry;

  const hasActiveTalent = isActiveTalentNode(talent);
  const isSelectedTalent =
    forceActive || (hasActiveTalent && (talent.viewIndex === undefined || talent.viewIndex === talent.entryIndex));

  const isTieredTalent = isTieredTalentNode(talent);
  const rankLabel = getTalentRankLabel(talent);
  const shape = shapeForTalentNode(talent, displayedEntryOptions);
  const apexSatelliteEntries = showApexTierIcons && !iconOnly ? getApexSatelliteTalentEntries(talent) : [];
  const primaryIconSize = apexSatelliteEntries.length > 0 ? getApexPrimaryIconSize(size) : size;

  const imgClassName = cx('rio-talent-icon', {
    'rio-talent-icon--default': !isSelectedTalent,
    'rio-talent-icon--dimmed': dimmed && !isSelectedTalent,
    'rio-talent-icon--show-added-icon': showAddedIcon === true,
    'rio-talent-icon--show-removed-icon': showRemovedIcon === true,
    'rio-talent-icon--border-added': border === 'added',
    'rio-talent-icon--border-removed': border === 'removed'
  });

  // If there is no spell return a placeholder icon/message.
  if (!spell) {
    return (
      <div
        title={`${t('common:errors.empty-talent')}`}
        className={cx(className, 'd-inline-block')}
        style={{ position: 'relative' }}
      >
        <WowIcon
          size={size}
          imgClassName={imgClassName}
          octagonContainerClassName={cx('rio-talent-icon--octagon-container', {
            'rio-talent-icon--octagon-container--default': !isSelectedTalent
          })}
          icon="inv_misc_questionmark"
        />
      </div>
    );
  }

  const octagonContainerClassName = cx('rio-talent-icon--octagon-container', {
    'rio-talent-icon--octagon-container--default': !isSelectedTalent,
    'rio-talent-icon--octagon-container--dimmed': dimmed && !isSelectedTalent,
    'rio-talent-icon--octagon-container--show-added-icon': showAddedIcon,
    'rio-talent-icon--octagon-container--show-removed-icon': showRemovedIcon,
    'rio-talent-icon--octagon-container--border-added': border === 'added',
    'rio-talent-icon--octagon-container--border-removed': border === 'removed'
  });

  const wowIconContent = (
    <>
      <WowIcon
        size={primaryIconSize}
        imgClassName={imgClassName}
        octagonContainerClassName={octagonContainerClassName}
        shape={shape}
        icon={spell.icon}
      />

      {showAddedIcon && (
        <div className="rio-talent-icon__indicator rio-talent-icon__indicator--show-added-icon">
          <span className="rio-talent-icon__indicator-badge">
            <i className="fa fa-fw fa-check"></i>
          </span>
        </div>
      )}

      {showRemovedIcon && (
        <div className="rio-talent-icon__indicator rio-talent-icon__indicator--show-removed-icon">
          <span className="rio-talent-icon__indicator-badge">
            <i className="fa fa-fw fa-xmark"></i>
          </span>
        </div>
      )}

      {compareBadge ? (
        <div
          className={cx(
            'rio-talent-compare-badge',
            `rio-talent-compare-badge--${primaryIconSize}`,
            `rio-talent-compare-badge--shape-${shape}`,
            `rio-talent-compare-badge--${compareBadge.tone}`
          )}
        >
          <span className="rio-talent-compare-badge__data">{compareBadge.label}</span>
        </div>
      ) : null}

      {(displayedEntry.maxRanks > 1 || isTieredTalent) && isSelectedTalent && !badge && !hideRanks && rankLabel && (
        <div
          className={cx('rio-talent-rank--container', `rio-talent-rank--container--${primaryIconSize}`, {
            'rio-talent-rank--container--show-added-icon': showAddedIcon,
            'rio-talent-rank--container--show-removed-icon': showRemovedIcon,
            'rio-talent-rank--container--show-compare-badge': Boolean(compareBadge)
          })}
        >
          <span className="rio-talent-rank--data">{rankLabel}</span>
        </div>
      )}

      {badge && (
        <div
          className={cx('rio-talent-badge', `rio-talent-badge--${primaryIconSize}`, {
            'rio-talent-badge--default': !isSelectedTalent,
            'rio-talent-badge--dimmed': dimmed && !isSelectedTalent,
            'rio-talent-badge--hoverable': !iconOnly
          })}
        >
          <span className="rio-talent-badge--data">{badge}</span>
        </div>
      )}

      {rankDiff != null && rankDiff !== 0 && (
        <div
          className={cx('rio-talent-rank-diff', `rio-talent-rank-diff--${primaryIconSize}`, {
            'rio-talent-rank-diff--increased':
              addedRankDiffVariant === 'increased' ? rankDiff > 0 : !addedRankDiffVariant && rankDiff > 0,
            'rio-talent-rank-diff--decreased': rankDiff < 0,
            'rio-talent-rank-diff--highlight': addedRankDiffVariant === 'highlight' && rankDiff > 0
          })}
        >
          <span className="rio-talent-rank-diff--data">
            {rankDiff > 0 ? '+' : ''}
            {rankDiff}
          </span>
        </div>
      )}

      {showArrows && isChoiceNode(talent.node) && (
        <div
          className={cx('rio-talent-arrows--container', `rio-talent-arrows--container--${primaryIconSize}`, {
            'rio-talent-arrows--container--default': !isSelectedTalent,
            'rio-talent-arrows--container--dimmed': dimmed && !isSelectedTalent
          })}
        >
          <i className="arrow arrow--left"></i>
          <i className="arrow arrow--right"></i>
        </div>
      )}
      {/* useful for debugging whether nodes are showing properly */}
      {/* {<div className="slds-text-align--center" style={{ fontSize: 8 }}>{talent.node.col}, {talent.node.row} [{talent.node.diff}]</div>} */}
    </>
  );

  if (iconOnly) {
    return (
      <div className={cx(className, 'd-inline-block')} style={{ position: 'relative' }}>
        {wowIconContent}
      </div>
    );
  }

  if (apexSatelliteEntries.length > 0) {
    return (
      <div className={cx(className, 'd-inline-block', 'rio-apex-talent-icon')} style={{ position: 'relative' }}>
        <WowheadLink
          type="spell"
          id={spell.id}
          def={displayedEntry.traitDefinitionId}
          className="rio-apex-talent-primary-link d-inline-block"
          style={{ position: 'relative' }}
        >
          {wowIconContent}
        </WowheadLink>
        <ApexTalentSatelliteIcons
          talent={talent}
          entries={apexSatelliteEntries}
          size={primaryIconSize}
          dimmed={dimmed}
        />
      </div>
    );
  }

  return (
    <WowheadLink
      type="spell"
      id={spell.id}
      className={cx(className, 'd-inline-block')}
      style={{ position: 'relative' }}
    >
      {wowIconContent}
    </WowheadLink>
  );
};

function ApexTalentSatelliteIcons({
  talent,
  entries,
  size,
  dimmed
}: {
  talent: TTalentWithViewIndex;
  entries: TApexSatelliteTalentEntry[];
  size: TWowIconSize;
  dimmed: boolean;
}) {
  const satelliteSize = getApexSatelliteIconSize(size);

  return (
    <div className="rio-apex-talent-satellites">
      {entries.map((satellite, index) => {
        const spell = satellite.entry.spell;
        if (!spell) {
          return null;
        }

        const isSelected = satellite.active || talent.grantedNode === true;
        const imgClassName = cx('rio-talent-icon', {
          'rio-talent-icon--default': !isSelected,
          'rio-talent-icon--dimmed': dimmed && !isSelected
        });

        return (
          <WowheadLink
            key={`${satellite.entry.id}-${satellite.entryRank}`}
            type="spell"
            id={spell.id}
            def={satellite.entry.traitDefinitionId}
            rank={satellite.linkRank}
            className={cx('rio-apex-talent-satellite', {
              'rio-apex-talent-satellite--inactive': !isSelected
            })}
            style={getApexSatelliteStyle(index, entries.length, size)}
          >
            <WowIcon size={satelliteSize} imgClassName={imgClassName} shape="circle" icon={satellite.displayIcon} />
          </WowheadLink>
        );
      })}
    </div>
  );
}

function getApexPrimaryIconSize(size: TWowIconSize): TWowIconSize {
  switch (size) {
    case 'xxx-small':
    case 'xx-small':
    case 'x-small':
    case 'small':
      return 'medium';
    case 'small-medium':
    case 'medium':
    case 'large':
    case 'x-large':
      return size;
  }
}

function getApexSatelliteIconSize(size: TWowIconSize): TWowIconSize {
  switch (size) {
    case 'x-large':
    case 'large':
      return 'x-small';
    case 'medium':
    case 'small-medium':
    case 'small':
      return 'xx-small';
    case 'xxx-small':
    case 'xx-small':
    case 'x-small':
      return 'xxx-small';
  }
}

function getApexSatelliteStyle(index: number, count: number, size: TWowIconSize): CSSProperties {
  const startAngle = -140;
  const endAngle = -40;
  const angle = count === 1 ? -90 : startAngle + ((endAngle - startAngle) * index) / (count - 1);
  const radians = (angle * Math.PI) / 180;
  const radius = getApexSatelliteRadius(size);

  return {
    '--rio-apex-satellite-x': `${Math.cos(radians) * radius}px`,
    '--rio-apex-satellite-y': `${Math.sin(radians) * radius}px`
  } as CSSProperties;
}

function getApexSatelliteRadius(size: TWowIconSize): number {
  switch (size) {
    case 'xxx-small':
    case 'xx-small':
    case 'x-small':
      return 15;
    case 'small':
      return 18;
    case 'small-medium':
      return 22;
    case 'medium':
      return 25;
    case 'large':
      return 32;
    case 'x-large':
      return 40;
  }
}

enum ENodeEntryType {
  SpendHex = 0,
  SpendSquare = 1,
  SpendCircle = 2,
  SpendSmallCircle = 3,
  DeprecatedSelect = 4,
  DragAndDrop = 5,
  SpendDiamond = 6,
  ProfPath = 7,
  ProfPerk = 8,
  ProfPathUnlock = 9
}

enum ETraitNodeType {
  Single = 0,
  Tiered = 1,
  Selection = 2
}

function shapeForTalentNode(talent: TTalentWithViewIndex, displayedEntryOptions: TDisplayedTalentEntryOptions = {}) {
  const nodeType = talent.node.type;
  const nodeEntryType = getDisplayedTalentEntry(talent, displayedEntryOptions).type;

  if (isTieredTalentNode(talent)) {
    return 'circle';
  }

  if (nodeType === ETraitNodeType.Selection) {
    return 'octagon';
  } else if (nodeEntryType === ENodeEntryType.SpendCircle) {
    return 'circle';
  } else {
    return 'square';
  }
}
